<template>
  <section
    class="new-highlights-section"
    :class="[
      `new-highlights-section--${screenOrientationType}`,
      { 'new-highlights-section--edit': createHighlight },
      { 'new-highlights-section--is-pc': isPc },
    ]"
  >
    <button
      v-if="!createHighlight && showCreateHighlightButton"
      class="new-highlights-section__add-button"
      :class="{ 'new-highlights-section__add-button--disabled': disabledAddButton }"
      :disabled="disabledAddButton"
      @click="enableHighlightCardCreate"
    >
      <svg
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          fill-rule="evenodd"
          clip-rule="evenodd"
          d="M11 13V22H13V13H22V11H13V2H11V11H2V13H11Z"
          fill="#2B2B2B"
        />
      </svg>
    </button>

    <div
      class="new-highlights-section__mask"
      :class="{ 'new-highlights-section__mask--active': createHighlight }"
      @click.stop="() => {}"
    />

    <div
      class="new-highlights-section__wrapper"
      :class="{ 'new-highlights-section__wrapper--active': createHighlight }"
    ></div>
    <transition name="slide" appear>
      <new-highlights-input-section
        v-if="createHighlight && highlightData"
        :videoPlayerKey="videoPlayerKey"
        class="new-highlights-section__create"
        :screenOrientationType="screenOrientationType"
        :highlightData="highlightData"
        @close="hideHighlightCardCreate"
        @onSaveHighlight="saveHighlight"
        @showHighlightPreviewVideoPlayer="showHighlightPreviewVideoPlayer"
        @hideHighlightPreviewVideoPlayer="hideHighlightPreviewVideoPlayer"
      />
    </transition>
  </section>
</template>

<script lang="ts">
import { defineComponent, PropType, computed } from '@vue/composition-api'
import NewHighlightsInputSection from '@/components/RaceVideoPage/RaceVideoHighlightsPane/NewHighlightsInputSection.vue'
import { LockOrientationType } from '@/views/RaceVideoPage/RaceVideoPage.vue'
import { EditHighlightData } from '@/store/stores/pageStore/RaceVideoPage/RaceVideoPageStore'
import DeviceInfo from '@/util/DeviceInfo'
import HighlightDocument from '@/store/stores/collectionModule/documents/highlight/HighlightDocument'
import usePermission from '@/components/hook/usePermission'
import StoreUtil from '@/store/StoreUtil'

/**
 * レース動画再生画面 ハイライト新規作成 セクション
 */
export default defineComponent({
  name: 'NewHighlightsSection',
  components: {
    NewHighlightsInputSection,
  },
  props: {
    /**
     * 画面向き
     */
    screenOrientationType: {
      type: String,
      default: 'portrait-primary',
    },
    /**
     * 画面固定
     */
    lockOrientation: {
      type: String as PropType<LockOrientationType>,
      default: 'landscape',
    },
    /**
     * ハイライトの作成・編集で使用するデータ
     */
    highlightData: {
      type: Object as PropType<EditHighlightData>,
    },
    /**
     * ハイライト作成・編集モーダルの表示/非表示
     */
    createHighlight: {
      type: Boolean,
      default: false,
    },
    /**
     * プレビュー動画プレイヤー再描画用のキー
     */
    videoPlayerKey: {
      type: Number,
      required: true,
    },
  },
  setup(_props, context) {
    const raceVideoPageStore = StoreUtil.useStore('RaceVideoPageStore')
    const { isCreateHighlightAvailable } = raceVideoPageStore
    const { hasPermission } = usePermission()

    const disabledAddButton = computed(() => !isCreateHighlightAvailable.value)

    const isPc = DeviceInfo.isForPC()
    const showCreateHighlightButton = computed(() => hasPermission('createHighlight'))

    const enableHighlightCardCreate = () => {
      context.emit('openHighlightsModal')
    }

    const hideHighlightCardCreate = () => {
      context.emit('onClickConfirmCancel')
    }

    return {
      disabledAddButton,
      isPc,
      showCreateHighlightButton,
      enableHighlightCardCreate,
      hideHighlightCardCreate,
    }
  },
  methods: {
    /**
     * 動画プレイヤーを表示する
     */
    showHighlightPreviewVideoPlayer() {
      this.$emit('showHighlightPreviewVideoPlayer')
    },
    /**
     * 動画プレイヤーを非表示にする
     */
    hideHighlightPreviewVideoPlayer() {
      this.$emit('hideHighlightPreviewVideoPlayer')
    },
    saveHighlight(
      comment: string,
      highlightData: HighlightDocument,
      postX: boolean,
      postInstagram: boolean,
    ) {
      this.$emit('onSaveHighlight', comment, highlightData, postX, postInstagram)
    },
  },
})
</script>

<style lang="scss" scoped>
.new-highlights-section {
  position: fixed;
  bottom: 0;
  right: 0;
  width: 100%;
  z-index: 40;

  &__mask {
    position: absolute;
    right: 0;
    bottom: 0;
    left: 0;
    background: $black50;
    width: 100%;
    height: 100vh;
    z-index: 41;
    opacity: 0;
    pointer-events: none;
    transition: opacity ease 0.3s;

    &--active {
      opacity: 1;
      pointer-events: auto;
    }
  }

  &__add-button {
    position: absolute;
    right: 12px;
    bottom: calc(var(--safe-area-inset-bottom) + 46px);
    display: flex;
    align-items: center;
    justify-content: center;
    width: 52px;
    height: 52px;
    border-radius: 50%;
    background: $green-light;
    box-shadow: 0 1px 0 0 $black05;

    &--disabled {
      opacity: 0.6;
      background: $gray50;

      svg path {
        fill: $black20;
      }
    }
  }

  &--landscape-primary,
  &--landscape-secondary {
    display: none;
  }

  &--is-pc {
    .new-highlights-section__wrapper--active {
      position: fixed;
      top: 0;
      right: 0;
      bottom: 0;
      left: 0;
      background-color: $black70;
    }
  }
}

.slide-enter-active,
.slide-leave-active {
  transition: all ease 0.3s;
}

.slide-enter,
.slide-leave-to {
  transform: translateY(100%);
  opacity: 0;
}
</style>
